<template>
  <div
    :class="{
      'insight-modal': true,
      'modal-mask': insightModalStore.openAsModal
    }"
  >
    <div ref="modalContainer" v-on-click-outside="insightModalStore.setCloseViewAll">
      <section v-if="!insightModalStore.layoutIsFullList">
        <InsightListPreviewFilters />
      </section>
      <section v-else class="insight-modal__top-section">
        <InsightListFullFilters>
          <template #close>
            <span v-if="insightModalStore.openAsModal" class="d-flex justify-content-end">
              <button
                class="btn pe-0"
                style="padding: 10px"
                @click="insightModalStore.setCloseViewAll()"
              >
                <IconX />
              </button>
            </span>
          </template>
        </InsightListFullFilters>
      </section>

      <div v-if="!isLoading" class="d-grid">
        <template v-if="insightModalStore.isGrid && !insightModalStore.layoutIsFullList">
          <div class="insight-modal__slider-wrapper">
            <div
              ref="scrollRef"
              :class="{
                'insight-modal__slider d-flex': true,
                'full-list': insightModalStore.layoutIsFullList
              }"
              @touchstart="onTouchStart"
              @touchmove="onTouchMove"
              @touchend="handleSwipe"
            >
              <InsightsCard v-for="item in insightModalStore.items" :key="item.id" :item="item" />
            </div>
            <div
              class="insight-modal__slider__overlay-left"
              :style="{ display: showLeftArrow ? 'inherit' : 'none' }"
            />
            <div
              class="insight-modal__slider__overlay-right"
              :style="{ display: showRightArrow ? 'inherit' : 'none' }"
            />
            <div
              class="insight-modal__slider__arrow-left"
              :style="{ display: showLeftArrow ? 'inherit' : 'none' }"
              @click="onArrowLeft"
            />
            <div
              class="insight-modal__slider__arrow-right"
              :style="{ display: showRightArrow ? 'inherit' : 'none' }"
              @click="onArrowRight"
            />
          </div>
        </template>
        <div
          v-if="insightModalStore.isGrid"
          :class="{
            'insight-modal__grid': true,
            'd-grid overflow-auto full-list': insightModalStore.layoutIsFullList,
            '-preview': !insightModalStore.layoutIsFullList
          }"
          :style="`min-height: ${gridMinHeight}px`"
        >
          <InsightsCard v-for="item in insightModalStore.items" :key="item.id" :item="item" />
        </div>

        <div v-else class="insight-modal__table">
          <InsightModalTable />
        </div>
        <div class="insight-modal__pager-wrapper">
          <div class="insight-modal-pager">
            <Pager
              v-if="
                insightModalStore.layoutIsFullList &&
                insightModalStore.openAsModal &&
                insightModalStore.items.length < insightModalStore.totalResults
              "
            />
          </div>
          <div
            v-if="
              insightModalStore.layoutIsFullList &&
              insightModalStore.openAsModal &&
              insightModalStore.items.length < insightModalStore.totalResults
            "
            class="insight-modal-selection"
          >
            <span class="insight-modal-selection__label">Results</span>
            <select class="form-select insight-modal-size-select" v-model="insightModalStore.size">
              <option v-for="size in [8, 12, 16, 20]" :key="size" :value="size">{{ size }}</option>
            </select>
          </div>
        </div>
      </div>
      <div class="insight-modal-more">
        <button
          v-if="
            insightModalStore.layoutIsFullList &&
            !insightModalStore.openAsModal &&
            insightModalStore.items.length < insightModalStore.totalResults
          "
          :class="`btn btn-manatt-black mt-3`"
          @click="loadMore()"
        >
          Load More...
        </button>
      </div>
      <div v-show="isLoading">
        <div
          :class="{
            'insight-modal__grid': true,
            'd-grid overflow-auto full-list': insightModalStore.layoutIsFullList,
            '-preview': !insightModalStore.layoutIsFullList
          }"
        >
          <PlaceholderInsightsCard />
          <PlaceholderInsightsCard />
          <PlaceholderInsightsCard />
          <PlaceholderInsightsCard />
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { useInsightModalStore } from '~/stores/modals/insight-modal'
import { vOnClickOutside } from '@vueuse/components'
import delay from 'lodash/delay'
import { useEventListener } from '@vueuse/core'

const pageStore = usePageStore()
const insightModalStore = useInsightModalStore()
const { isLoading, items } = storeToRefs(insightModalStore)
const gridMinHeight = computed(() => {
  if (items.value.length == 0) return 0
  if (items.value.length > 2) return 1060
  else return 530
})

const modalContainer = ref<HTMLElement | null>()

const themeColor = pageStore.getThemeColor()
const btnColor = themeColor == 'gold' ? 'black' : themeColor

const scrollAmount = 300
const { scrollRef, scrollLeft, scrollRight } = useScroll()
const { showLeftArrow, showRightArrow, checkVisibility } = useSliderVisibility(scrollRef)
const updateArrows = () => delay(checkVisibility, 500)
const { onTouchStart, onTouchMove, onTouchEnd } = useSwipe()

const handleSwipe = () => {
  onTouchEnd((direction) => {
    if (scrollRef.value) {
      if (direction === 'right') {
        scrollLeft(scrollAmount)
        updateArrows()
      } else if (direction === 'left') {
        scrollRight(scrollAmount)
        updateArrows()
      }
    }
  })
}

const onArrowLeft = () => {
  scrollLeft(scrollAmount)
  updateArrows()
}

const onArrowRight = () => {
  scrollRight(scrollAmount)
  updateArrows()
}

const showModal = () => {
  document.body.classList.add('modal-open')
  modalContainer.value?.classList.add('modal-container')
  delay(() => modalContainer.value?.classList.add('show'), 100)
}

const hideModal = () => {
  document.body.classList.remove('modal-open')
  modalContainer.value?.classList.remove('modal-container')
  modalContainer.value?.classList.remove('show')
}

watch(
  () => insightModalStore.openAsModal,
  (isOpenAsModal) => {
    if (isOpenAsModal) {
      showModal()
    } else {
      hideModal()
    }
  }
)

function loadMore() {
  insightModalStore.setReadMore(true)
}

onMounted(() => {
  updateArrows()
  useEventListener(window, 'scroll', updateArrows)
})
onUnmounted(() => hideModal())
</script>
